import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Header, Footer } from "@/components/site-chrome";
import { FadeImg } from "@/components/fade-img";
import { memo, useState, useEffect } from "react";
import { useCart } from "@/context/CartContext";
import { usePostHog } from "@posthog/react";

const API = import.meta.env.VITE_API_URL || "https://pizzasteve-api.m-2396.workers.dev";

interface Product {
  id: string;
  name: string;
  size?: string;
  price?: number;
  priceLabel?: string;
  status: string;
  emoji: string;
  tag: string;
  imageUrl?: string;
  images?: string[];
  condition?: string;
  description?: string;
}

export const Route = createFileRoute("/shop")({
  head: () => ({
    meta: [
      { title: "Shop - Mr. Pizza Steve Finds" },
      { name: "description", content: "Browse the current drop: tees, jorts, eyewear, Harley Davidson and more vintage finds in Zamalek, Cairo." },
      { property: "og:title", content: "Shop - Mr. Pizza Steve Finds" },
    ],
  }),
  component: Shop,
});

function cloudImg(url: string, width = 600) {
  if (!url || !url.includes("cloudinary.com")) return url;
  return url.replace("/upload/", `/upload/w_${width},f_auto,q_auto/`);
}

const ALL = "ALL";
const TOP_TAGS = ["TEE", "SHIRT", "HOODIE", "JACKET", "TOPS"];
const BOTTOM_TAGS = ["JORTS", "PANTS", "SHORTS", "BOTTOMS", "DENIM"];

function Shop() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState(ALL);
  const [hideSoldOut, setHideSoldOut] = useState(false);
  const posthog = usePostHog();

  useEffect(() => {
    fetch(`${API}/api/products`)
      .then((r) => r.json())
      .then((data) => setProducts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const allTags = Array.from(new Set(products.map((p) => p.tag).filter(Boolean)));
  const hasTops = allTags.some(t => TOP_TAGS.includes((t || "").toUpperCase()));
  const hasBottoms = allTags.some(t => BOTTOM_TAGS.includes((t || "").toUpperCase()));
  const otherTags = allTags.filter(t => !TOP_TAGS.includes((t || "").toUpperCase()) && !BOTTOM_TAGS.includes((t || "").toUpperCase()));
  const tags = [ALL, ...(hasTops ? ["TOPS"] : []), ...(hasBottoms ? ["BOTTOMS"] : []), ...otherTags];

  const filtered = products.filter((p) => {
    if (hideSoldOut && p.status === "sold") return false;
    if (activeTag === ALL) return true;
    if (activeTag === "TOPS") return TOP_TAGS.includes((p.tag || "").toUpperCase());
    if (activeTag === "BOTTOMS") return BOTTOM_TAGS.includes((p.tag || "").toUpperCase());
    return p.tag === activeTag;
  });

  return (
    <div className="min-h-screen">
      <Header />

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        {/* Page header */}
        <div className="border-b border-border pb-10 mb-10">
          <p className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground mb-3">The Shop</p>
          <h1 className="text-4xl uppercase tracking-[-0.02em] sm:text-6xl">Current Drop</h1>
          <p className="mt-4 text-xs text-muted-foreground">
            Add to cart to reserve before some other guy takes it.
          </p>
        </div>

        {/* Filter controls */}
        {!loading && (
          <div className="flex flex-wrap items-center gap-2 mb-10">
            {tags.length > 1 && tags.map((tag) => (
              <button
                key={tag}
                onClick={() => {
                  setActiveTag(tag);
                  posthog.capture("shop_filter_applied", { filter_tag: tag });
                }}
                className={`text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors duration-150 ${
                  activeTag === tag
                    ? "border-foreground bg-foreground text-background"
                    : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                }`}
              >
                {tag}
              </button>
            ))}
            <button
              onClick={() => setHideSoldOut(v => !v)}
              className={`ml-auto text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors duration-150 ${
                hideSoldOut
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
              }`}
            >
              {hideSoldOut ? "Show Sold Out" : "Hide Sold Out"}
            </button>
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:gap-8 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-background border border-border rounded-xl overflow-hidden animate-pulse">
                <div className="aspect-square bg-muted" />
                <div className="border-t border-border p-4 space-y-2">
                  <div className="h-2 bg-muted rounded w-2/3" />
                  <div className="h-2 bg-muted rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:gap-8 sm:grid-cols-3 lg:grid-cols-4">
            {filtered.map((p, index) => <Card key={p.id} p={p} index={index} />)}
          </div>
        ) : (
          <div className="mt-16 text-center">
            <p className="text-xs text-muted-foreground">Nothing in this category yet. Check back soon.</p>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

const Card = memo(function Card({ p, index }: { p: Product; index: number }) {
  const sold = p.status === "sold";
  const cart = useCart();
  const navigate = useNavigate();
  const posthog = usePostHog();

  return (
    <Link
      to="/product/$id"
      params={{ id: p.id }}
      style={{ animationDelay: `${Math.min(index * 40, 240)}ms` }}
      className={`group relative z-0 bg-background border border-border rounded-xl overflow-hidden animate-fade-slide-up transition-all duration-300 ease-out ${sold ? "opacity-50" : "hover:-translate-y-1 hover:z-10 hover:border-foreground"}`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-card">
        {p.imageUrl ? (
          <FadeImg
            src={cloudImg(p.imageUrl, 400)}
            alt={p.name}
            className={`h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05] ${sold ? "grayscale" : ""}`}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-6xl">{p.emoji}</div>
        )}
        <span className="absolute left-2 top-2 text-[9px] uppercase tracking-[0.15em] text-muted-foreground bg-background/70 px-1.5 py-0.5">
          {p.tag}
        </span>
        {sold && (
          <span className="absolute right-2 top-2 text-[9px] uppercase tracking-[0.15em] text-muted-foreground bg-background/70 px-1.5 py-0.5">
            Sold
          </span>
        )}
      </div>

      {/* Info */}
      <div className="border-t border-border p-3">
        <p className="line-clamp-1 text-[10px] uppercase tracking-[0.1em] text-foreground">{p.name}</p>
        <div className="mt-1 flex items-center justify-between">
          <span className="text-[10px] text-foreground">
            {p.price ? `${p.price} EGP` : p.priceLabel}
          </span>
          {p.size && <span className="text-[9px] text-muted-foreground">sz {p.size}</span>}
        </div>

        {!sold && (
          <div className="mt-2 grid grid-cols-2 gap-1">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                cart.add({ id: p.id, name: p.name, price: p.price, priceLabel: p.priceLabel, imageUrl: p.imageUrl, size: p.size, emoji: p.emoji });
                posthog.capture("add_to_cart", {
                  product_id: p.id,
                  product_name: p.name,
                  product_tag: p.tag,
                  product_price: p.price,
                  product_size: p.size,
                  source: "shop_grid",
                });
              }}
              className="border border-foreground py-1.5 text-[9px] uppercase tracking-[0.15em] text-foreground transition-all active:scale-[0.98] hover:bg-foreground hover:text-background"
            >
              Add
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                cart.add({ id: p.id, name: p.name, price: p.price, priceLabel: p.priceLabel, imageUrl: p.imageUrl, size: p.size, emoji: p.emoji });
                posthog.capture("buy_now_clicked", {
                  product_id: p.id,
                  product_name: p.name,
                  product_tag: p.tag,
                  product_price: p.price,
                  product_size: p.size,
                  source: "shop_grid",
                });
                navigate({ to: "/checkout" });
              }}
              className="border border-border py-1.5 text-[9px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
            >
              Buy Now
            </button>
          </div>
        )}
      </div>
    </Link>
  );
});
